import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { API_ENDPOINTS, METHOD_TYPE } from "../../utils/apiUrls";
import api from "../../utils/api";

const extractDashboardData = (payload) => payload?.data?.data ?? payload?.data ?? payload ?? {};

const initialState = {
    isLoading: false,
    error: null,
    screeningPercentage: {},
    screeningPercentageClients: {},
    graphStats: {},
    keyStatsForGraph: [],
    percentageOnboarded: null,
    percentageScreening: null,
    corporateScreening: null,
    corporateOnboarded: null,
    geographicCounts: { rows: [], total: 0, region_count: 0 },
    sourceLists: null,
    // Oomero Pulse command center, for the selected date range. Kept apart from isLoading so the
    // widgets show their own loading state instead of the full-page loader.
    commandCenter: { status: 'idle', data: null, error: null, rangeKey: null },
}

export const getIndividualScreeningPercentages = createAsyncThunk("/dashboard/getIndividualScreeningPercentage", async (queryParam) => {
    try {
        const data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getIndividualScreeningPercentage + queryParam,
        }
        const response = await api(data);
        return response.data
    } catch (error) {
        throw error?.response?.data
    }
})

export const getKeyStatsForGraph = createAsyncThunk("/dashboard/getKeyStatsForGraph", async (queryParam) => {
    try {
        const data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getKeyStatsForGraph + queryParam,
        }
        const response = await api(data);
        return response.data
    } catch (error) {
        throw error?.response?.data
    }
})
export const getStatsByGraph = createAsyncThunk("/dashboard/getStatsByGraph", async (queryParam) => {
    try {
        const data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getStatsByGraph + queryParam,
        }
        const response = await api(data);
        return response.data
    } catch (error) {
        throw error?.response?.data
    }
})
export const getIndividualScreeningPercentagesClients = createAsyncThunk("/dashboard/getIndividualScreeningPercentages", async () => {
    try {
        const data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getIndividualScreeningPercentage,
        }
        const response = await api(data);
        return response.data
    } catch (error) {
        throw error?.response?.data
    }
})
export const getIndividualScreeningPercentageOnboarded = createAsyncThunk("/dashboard/getIndividualScreeningPercentage?section=onboarded", async () => {
    try {
        const data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getIndividualScreeningPercentageOnboarded,
        }
        const response = await api(data);
        return response.data
    } catch (error) {
        throw error?.response?.data
    }
})
export const getIndividualScreeningPercentageScreening = createAsyncThunk("/dashboard/getIndividualScreeningPercentage?section=screening", async () => {
    try {
        const data = {
            method: METHOD_TYPE.get,
            url: API_ENDPOINTS.getIndividualScreeningPercentageScreening,
        }
        const response = await api(data);
        return response.data
    } catch (error) {
        throw error?.response?.data
    }
})

export const fetchCorporateBucketStatus = createAsyncThunk("fetchCorporateBucketStatus", async (type) => {
    try {
        const data = {
            method: METHOD_TYPE.get,
            url: `${API_ENDPOINTS.getCorporateBucketStatus}?section=${type==="screening"? "screening" : "onboarded" }`,
        }
        const response = await api(data);
        return {type, data: response.data}
    } catch (error) {
        throw error?.response?.data
    }
})

export const fetchGeographicProfileCounts = createAsyncThunk("dashboard/fetchGeographicProfileCounts", async () => {
    try {
        const response = await api.get(API_ENDPOINTS.getGeographicProfileCounts);
        return extractDashboardData(response?.data);
    } catch (error) {
        throw error?.response?.data || error;
    }
});

export const fetchDashboardSourceLists = createAsyncThunk("dashboard/fetchSourceLists", async () => {
    try {
        const queries = [
            ["individualScreening", API_ENDPOINTS.getIndividualScreeninglist, "?limit=10&page=1&onboarded=false"],
            ["corporateScreening", API_ENDPOINTS.getCorporateScreeninglist, "?limit=10&page=1&onboarded=false"],
            ["individualOnboarded", API_ENDPOINTS.getIndividualScreeninglist, "?limit=10&page=1&onboarded=true&status=all"],
            ["corporateOnboarded", API_ENDPOINTS.getCorporateScreeninglist, "?limit=10&page=1&onboarded=true&status=all"],
        ];
        const result = {};
        for (const [key, url, query] of queries) {
            const response = await api.get(`${url}${query}`);
            result[key] = extractDashboardData(response?.data);
        }
        return result;
    } catch (error) {
        throw error?.response?.data || error;
    }
});

// Everything the command center counts over one date range, in one request.
export const fetchCommandCenter = createAsyncThunk("dashboard/fetchCommandCenter", async ({ from, to }) => {
    try {
        const response = await api.get(`${API_ENDPOINTS.getDashboardCommandCenter}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
        return extractDashboardData(response?.data);
    } catch (error) {
        throw error?.response?.data || error;
    }
});

const dashboardSlice = createSlice({
    name: "dashboard",
    initialState,
    reducers: {
    },
    extraReducers: (builder) => {
        builder
            .addCase(getIndividualScreeningPercentages.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.screeningPercentage = payload.data;
            })
            .addCase(getKeyStatsForGraph.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.keyStatsForGraph = payload.data;
            })
            .addCase(getStatsByGraph.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.graphStats = payload.data;
            })
            .addCase(getIndividualScreeningPercentageOnboarded.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.percentageOnboarded = extractDashboardData(payload);
            })
            .addCase(getIndividualScreeningPercentageScreening.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.percentageScreening = extractDashboardData(payload);
            })
            .addCase(getIndividualScreeningPercentagesClients.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.screeningPercentageClients = payload.data;
            })
            .addCase(fetchCorporateBucketStatus.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                if(payload?.type === "screening")
                    state.corporateScreening = extractDashboardData(payload.data);
                else
                    state.corporateOnboarded = extractDashboardData(payload.data);

            })
            .addCase(fetchDashboardSourceLists.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.sourceLists = payload;
            })
            .addCase(fetchCommandCenter.pending, (state, { meta }) => {
                state.commandCenter.status = 'loading';
                state.commandCenter.error = null;
                state.commandCenter.rangeKey = `${meta.arg.from}_${meta.arg.to}`;
            })
            .addCase(fetchCommandCenter.fulfilled, (state, { payload, meta }) => {
                if (state.commandCenter.rangeKey !== `${meta.arg.from}_${meta.arg.to}`) return;
                state.commandCenter.status = 'ready';
                state.commandCenter.data = payload;
            })
            .addCase(fetchCommandCenter.rejected, (state, { error, meta }) => {
                if (state.commandCenter.rangeKey !== `${meta.arg.from}_${meta.arg.to}`) return;
                state.commandCenter.status = 'error';
                state.commandCenter.error = error?.message || 'The dashboard could not be loaded.';
            })
            .addCase(fetchGeographicProfileCounts.fulfilled, (state, { payload }) => {
                state.isLoading = false;
                state.error = null;
                state.geographicCounts = payload;
            })
            .addMatcher(isAnyOf(getIndividualScreeningPercentages.pending, getKeyStatsForGraph.pending, getStatsByGraph.pending, getIndividualScreeningPercentagesClients.pending, getIndividualScreeningPercentageOnboarded.pending, getIndividualScreeningPercentageScreening.pending, fetchCorporateBucketStatus.pending, fetchDashboardSourceLists.pending, fetchGeographicProfileCounts.pending), (state) => {
                state.isLoading = true;
            })
            .addMatcher(isAnyOf(getIndividualScreeningPercentages.rejected, getKeyStatsForGraph.rejected, getStatsByGraph.rejected, getIndividualScreeningPercentagesClients.rejected, getIndividualScreeningPercentageOnboarded.rejected, getIndividualScreeningPercentageScreening.rejected, fetchCorporateBucketStatus.rejected, fetchDashboardSourceLists.rejected, fetchGeographicProfileCounts.rejected), (state, { error }) => {
                state.isLoading = false;
                state.error = error.message ? error.message : "Request Failed Please Try Again ";
            })
    }
});

const dashboardReducer = dashboardSlice.reducer;

export { dashboardReducer };
export default dashboardReducer;
